import { GameState, InningsState, WicketRecord } from '../types/game';

interface BatterStats {
  name: string;
  runs: number;
  balls: number;
  fours: number;
  sixes: number;
  isOut: boolean;
  dismissal?: string;
  strikeRate: number;
}

interface BowlerStats {
  name: string;
  overs: string;
  maidens: number;
  runs: number;
  wickets: number;
  economy: number;
}

interface InningsData {
  teamName: string;
  runs: number;
  wickets: number;
  overs: string;
  runRate: number;
  batters: BatterStats[];
  bowlers: BowlerStats[];
  extras: {
    byes: number;
    legByes: number;
    wides: number;
    noBalls: number;
    total: number;
  };
  fallOfWickets: Array<{
    wicket: number;
    runs: number;
    batter: string;
    overs: string;
  }>;
}

export function convertToScorecardData(gameState: GameState): {
  innings1?: InningsData;
  innings2?: InningsData;
} {
  const convertInnings = (innings: InningsState, teamName: string): InningsData => {
    // Calculate extras
    const extras = {
      byes: 0,
      legByes: 0,
      wides: 0,
      noBalls: 0,
      total: 0
    };

    // Count extras from all overs
    const allBalls = [...innings.previousOvers.flat(), ...innings.currentOverBalls];
    allBalls.forEach(ball => {
      if (ball === 'B') extras.byes++;
      else if (ball === 'LB') extras.legByes++;
      else if (ball === 'WD') extras.wides++;
      else if (ball === 'NB') extras.noBalls++;
    });
    extras.total = extras.byes + extras.legByes + extras.wides + extras.noBalls;

    // Calculate batters stats
    const batters: BatterStats[] = [];
    
    // Current batters
    if (innings.batter1.name !== "Last Man Stands") {
      const fours = allBalls.filter(ball => ball === 4).length;
      const sixes = allBalls.filter(ball => ball === 6).length;
      batters.push({
        name: innings.batter1.name,
        runs: innings.batter1.runs,
        balls: innings.batter1.balls,
        fours: Math.floor(fours / 2), // Rough estimate
        sixes: Math.floor(sixes / 2), // Rough estimate
        isOut: innings.batter1.isRetired,
        dismissal: innings.batter1.isRetired ? 'retired' : undefined,
        strikeRate: innings.batter1.balls > 0 ? parseFloat(((innings.batter1.runs / innings.batter1.balls) * 100).toFixed(1)) : 0
      });
    }

    if (innings.batter2.name !== "Last Man Stands") {
      const fours = allBalls.filter(ball => ball === 4).length;
      const sixes = allBalls.filter(ball => ball === 6).length;
      batters.push({
        name: innings.batter2.name,
        runs: innings.batter2.runs,
        balls: innings.batter2.balls,
        fours: Math.floor(fours / 2), // Rough estimate
        sixes: Math.floor(sixes / 2), // Rough estimate
        isOut: innings.batter2.isRetired,
        dismissal: innings.batter2.isRetired ? 'retired' : undefined,
        strikeRate: innings.batter2.balls > 0 ? parseFloat(((innings.batter2.runs / innings.batter2.balls) * 100).toFixed(1)) : 0
      });
    }

    // Add dismissed batters from wicket records
    innings.wicketRecords.forEach(wicket => {
      if (!batters.find(b => b.name === wicket.batterOut)) {
        batters.push({
          name: wicket.batterOut,
          runs: wicket.runs,
          balls: wicket.balls,
          fours: 0, // Would need to track this properly
          sixes: 0, // Would need to track this properly
          isOut: true,
          dismissal: `${wicket.dismissalType} ${wicket.bowler}${wicket.fielder ? ` c ${wicket.fielder}` : ''}`,
          strikeRate: wicket.balls > 0 ? parseFloat(((wicket.runs / wicket.balls) * 100).toFixed(1)) : 0
        });
      }
    });

    // Calculate bowlers stats
    const bowlers: BowlerStats[] = [];
    
    // Current bowler
    const currentBowler = innings.bowler;
    const bowlerOvers = formatOvers(currentBowler.ballsBowled, gameState.matchSettings.ballsPerOver);
    const economy = calculateEconomy(currentBowler.runsConceded, currentBowler.ballsBowled, gameState.matchSettings.ballsPerOver);
    
    bowlers.push({
      name: currentBowler.name,
      overs: bowlerOvers,
      maidens: 0, // Would need to track this properly
      runs: currentBowler.runsConceded,
      wickets: currentBowler.wicketsTaken,
      economy: parseFloat(economy)
    });

    // Calculate overs display
    const totalBalls = innings.totalBalls;
    const oversDisplay = formatOvers(totalBalls, gameState.matchSettings.ballsPerOver);

    // Calculate run rate
    const overs = totalBalls / gameState.matchSettings.ballsPerOver;
    const runRate = overs > 0 ? parseFloat((innings.runs / overs).toFixed(2)) : 0;

    // Fall of wickets
    const fallOfWickets = innings.wicketRecords.map((wicket, index) => ({
      wicket: index + 1,
      runs: wicket.runs,
      batter: wicket.batterOut,
      overs: formatOvers(wicket.overNumber * gameState.matchSettings.ballsPerOver, gameState.matchSettings.ballsPerOver)
    }));

    return {
      teamName,
      runs: innings.runs,
      wickets: innings.wicketsDown,
      overs: oversDisplay,
      runRate,
      batters,
      bowlers,
      extras,
      fallOfWickets
    };
  };

  const result: { innings1?: InningsData; innings2?: InningsData } = {};

  if (gameState.innings1) {
    result.innings1 = convertInnings(gameState.innings1, gameState.teamA);
  }

  if (gameState.innings2) {
    result.innings2 = convertInnings(gameState.innings2, gameState.teamB);
  }

  return result;
}

function formatOvers(balls: number, ballsPerOver: number): string {
  const overs = Math.floor(balls / ballsPerOver);
  const remainingBalls = balls % ballsPerOver;
  return remainingBalls > 0 ? `${overs}.${remainingBalls}` : `${overs}`;
}

function calculateEconomy(runs: number, balls: number, ballsPerOver: number): string {
  const overs = balls / ballsPerOver;
  return overs > 0 ? (runs / overs).toFixed(2) : '0.00';
}